import { motion } from "framer-motion";
import { COUNCILS, CBD } from "@/data/melbCouncils";

const BARS = [
  ["Whitehorse", 55],
  ["Monash", 39],
  ["Manningham", 36],
  ["Wyndham", 34],
  ["Knox", 34],
  ["Casey", 33],
];

const bubbleColor = (c) =>
  c >= 26 ? "#2f6b24" : c >= 13 ? "#5c9e46" : c >= 6 ? "#8fd67a" : "#b7dfa4";
const bubbleR = (c) => 5 + Math.sqrt(c) * 2.6;

const WhereInstalled = () => {
  return (
    <section
      data-testid="where-installed-section"
      className="bg-navy py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-glight mb-4 flex items-center gap-2">
            <span className="inline-block w-8 h-px bg-glight" />
            Where we've installed
          </p>
          <h2 className="font-serif font-bold text-white tracking-tight text-3xl md:text-[34px] leading-tight">
            365+ installs and counting. 130+ suburbs. One trusted team.
          </h2>
          <p className="mt-4 text-lg text-[#c3ccd8]">
            From Whitehorse in the east to Wyndham in the west, we've completed
            heat pump hot water installs across 27 councils in Greater
            Melbourne.
          </p>

          <div className="mt-10 space-y-4" data-testid="council-bars">
            {BARS.map(([name, count], i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="flex items-center gap-4"
                data-testid={`council-bar-${i}`}
              >
                <span className="w-28 shrink-0 text-sm font-medium text-white">
                  {name}
                </span>
                <span className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
                  <motion.span
                    initial={{ width: 0 }}
                    whileInView={{ width: `${(count / 55) * 100}%` }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                    className={`block h-full rounded-full ${i === 0 ? "bg-glight" : "bg-amber"}`}
                  />
                </span>
                <span className="w-8 text-right text-sm font-bold text-white">
                  {count}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="council-map-card"
          className="relative bg-[#DCEFF7] rounded-[20px] h-[420px] lg:h-[520px] overflow-hidden shadow-xl"
        >
          <svg
            viewBox="0 0 600 600"
            className="absolute inset-0 w-full h-full"
            role="img"
            aria-label="Map of Greater Melbourne councils sized by install count"
            data-testid="council-map"
          >
            {COUNCILS.map((c) => (
              <path
                key={c.name}
                d={c.d}
                fill="#CFE7F2"
                stroke="#B7D9E8"
                strokeWidth="1"
              />
            ))}
            {COUNCILS.filter((c) => c.count > 0).map((c) => (
              <circle
                key={`${c.name}-bubble`}
                cx={c.cx}
                cy={c.cy}
                r={bubbleR(c.count)}
                fill={bubbleColor(c.count)}
                fillOpacity="0.85"
                stroke="#ffffff"
                strokeWidth="1.5"
              />
            ))}
            <circle
              cx={CBD.x}
              cy={CBD.y}
              r="4.5"
              fill="#011B47"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <text
              x={CBD.x - 10}
              y={CBD.y + 4}
              fontSize="13"
              fontWeight="700"
              fill="#011B47"
              textAnchor="end"
              stroke="#DCEFF7"
              strokeWidth="3"
              paintOrder="stroke"
            >
              CBD
            </text>
          </svg>

          <div
            data-testid="map-stat-card"
            className="absolute top-4 right-4 bg-navy rounded-xl px-4 py-3 shadow-lg"
          >
            <p className="font-serif font-bold text-white text-2xl leading-none">
              365+
            </p>
            <p className="text-xs text-[#c3ccd8] mt-1">installs and counting</p>
          </div>

          <div
            data-testid="map-legend"
            className="absolute bottom-4 left-4 bg-navy rounded-full px-4 py-2 flex items-center gap-2 shadow-lg"
          >
            <span className="text-[11px] text-white/80">Fewer</span>
            <span className="flex items-end gap-1" aria-hidden="true">
              <span className="inline-block w-2 h-2 rounded-full bg-[#b7dfa4]" />
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#8fd67a]" />
              <span className="inline-block w-3 h-3 rounded-full bg-[#5c9e46]" />
              <span className="inline-block w-3.5 h-3.5 rounded-full bg-[#2f6b24]" />
            </span>
            <span className="text-[11px] text-white/80">More installs</span>
          </div>

          <div
            data-testid="map-caption"
            className="absolute bottom-4 right-4 bg-navy rounded-full px-4 py-2 shadow-lg"
          >
            <span className="text-[11px] text-white/80">
              Bubble size &amp; shade = installs per council.
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhereInstalled;
